import { Controller } from 'stimulus';

// A Dropdown's or Multi-select's options as a list (shared/_options_editor):
// a row per option, written back to the comma-separated dropdown_options in
// the hidden value input. Enter adds what's typed and keeps the box ready for
// the next one; a pasted list (lines, tabs, or commas) adds each; an option
// already listed is skipped and its row flashed. Options are stored
// comma-separated, so a comma typed into one starts the next.
export default class extends Controller {
  static targets = ['value', 'list', 'input', 'message', 'row'];

  connect() {
    this.render();
    if (typeof $ !== 'undefined' && $.fn && $.fn.sortable) {
      $(this.listTarget).sortable({
        items: '> .opt-ed__row',
        handle: '.opt-ed__handle',
        axis: 'y',
        tolerance: 'pointer',
        update: () => this.sync(),
      });
    }
  }

  disconnect() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.sortable && $(this.listTarget).sortable('instance')) {
      $(this.listTarget).sortable('destroy');
    }
  }

  split(text) {
    return (text || '').split(/[\n\r\t,]+/).map((s) => s.trim()).filter(Boolean);
  }

  render() {
    this.listTarget.innerHTML = '';
    this.split(this.valueTarget.value).forEach((label) => this.listTarget.appendChild(this.buildRow(label)));
  }

  buildRow(label) {
    const row = this.rowTarget.content.firstElementChild.cloneNode(true);
    row.querySelector('.opt-ed__text').value = label;
    row.querySelector('.opt-ed__remove').setAttribute('aria-label', `Remove ${label}`);
    return row;
  }

  // ----- adding -----

  addOnEnter(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.add();
  }

  addOnComma() {
    if (this.inputTarget.value.includes(',')) this.add();
  }

  paste(event) {
    const text = (event.clipboardData || window.clipboardData).getData('text');
    if (!/[\n\r\t,]/.test(text)) return;

    // A list: whatever was already typed runs into its first line.
    event.preventDefault();
    const typed = this.inputTarget.value;
    this.inputTarget.value = '';
    this.addAll(this.split(typed + text));
  }

  add() {
    const items = this.split(this.inputTarget.value);
    this.inputTarget.value = '';
    if (items.length) this.addAll(items);
  }

  addAll(items) {
    const skipped = [];
    let added = 0;
    items.forEach((label) => {
      const existing = this.rowFor(label);
      if (existing) {
        skipped.push(label);
        this.flash(existing);
        return;
      }
      this.listTarget.appendChild(this.buildRow(label));
      added += 1;
    });

    if (skipped.length === 1) this.say(`"${skipped[0]}" is already in the list.`);
    else if (skipped.length > 1) this.say(`${skipped.length} options were already in the list.`);
    else this.say(added > 1 ? `Added ${added} options.` : '');
    this.listTarget.scrollTop = this.listTarget.scrollHeight;
    this.sync();
  }

  rowFor(label) {
    const key = label.toLowerCase();
    return this.rows().find((row) => row.querySelector('.opt-ed__text').value.trim().toLowerCase() === key);
  }

  // ----- editing a row -----

  edit(event) {
    const input = event.currentTarget;
    if (input.value.includes(',')) {
      const [first, ...rest] = this.split(input.value);
      input.value = first || '';
      let after = input.closest('.opt-ed__row');
      rest.forEach((label) => {
        if (this.rowFor(label)) return;
        const row = this.buildRow(label);
        after.after(row);
        after = row;
      });
    }
    this.sync();
  }

  rowKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.inputTarget.focus();
    } else if (event.key === 'Backspace' && event.currentTarget.value === '') {
      event.preventDefault();
      const row = event.currentTarget.closest('.opt-ed__row');
      const previous = row.previousElementSibling;
      row.remove();
      this.sync();
      (previous ? previous.querySelector('.opt-ed__text') : this.inputTarget).focus();
    }
  }

  dropIfEmpty(event) {
    if (event.currentTarget.value.trim()) return;

    event.currentTarget.closest('.opt-ed__row').remove();
    this.sync();
  }

  remove(event) {
    event.currentTarget.closest('.opt-ed__row').remove();
    this.say('');
    this.sync();
    this.inputTarget.focus();
  }

  // ----- writing back -----

  rows() {
    return Array.from(this.listTarget.querySelectorAll('.opt-ed__row'));
  }

  sync() {
    const labels = this.rows().map((row) => row.querySelector('.opt-ed__text').value.trim()).filter(Boolean);
    this.valueTarget.value = labels.join(', ');
    this.valueTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  say(text) {
    if (this.hasMessageTarget) this.messageTarget.textContent = text;
  }

  flash(row) {
    row.classList.remove('is-flash');
    // Restart the animation on a row flashed twice in a row.
    void row.offsetWidth; // eslint-disable-line no-void
    row.classList.add('is-flash');
  }
}
